import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  BulkCancelEventResponse,
  BulkReportEventResponse,
  EventInfo,
  ReportActionInvocationCompletedOptions,
  ReportEventApplicationErrors,
  ReportEventOptions,
  ReportEventResponse,
  RerunActivationOptions,
  RerunActivationResponse,
  StatusWithLiterals,
  bulkCancelEvent as universalBulkCancelEvent,
  bulkReportEvent as universalBulkReportEvent,
  cancelEvent as universalCancelEvent,
  reportActionInvocationCompleted as universalReportActionInvocationCompleted,
  reportEvent as universalReportEvent,
  rerunActivation as universalRerunActivation,
} from './automations-v2-activation-activations.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/automations' };

export function reportEvent(httpClient: HttpClient): ReportEventSignature {
  return (triggerKey: string, options?: ReportEventOptions) =>
    universalReportEvent(
      triggerKey,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ReportEventSignature {
  /**
   * Reports an event and activates site automations with the specified trigger key.
   *
   *
   * Only the app that created a trigger can report events for it.
   * This means other apps can't report events for your triggers,
   * and you can't report events for another app's triggers.
   *
   * If your app supports canceling events,
   * `externalEntityId` must be provided.
   * `externalEntityId` is required when calling [cancelEvent()](#cancel-event).
   * @param - Trigger key as defined in your app's trigger configuration
   * in the app dashboard.
   * For example, `form_submitted` or `invoice_due`.
   */
  (triggerKey: string, options?: ReportEventOptions): Promise<
    NonNullablePaths<ReportEventResponse, `activationIds`, 2> & {
      __applicationErrorsType?: ReportEventApplicationErrors;
    }
  >;
}

export function bulkReportEvent(
  httpClient: HttpClient
): BulkReportEventSignature {
  return (triggerKey: string, eventsInfo: EventInfo[]) =>
    universalBulkReportEvent(
      triggerKey,
      eventsInfo,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkReportEventSignature {
  /**
   * Bulk reports events and activates site automations with the specified trigger key.
   * @param - Trigger key as defined in your app's trigger configuration
   * in the app dashboard.
   * For example, `form_submitted` or `invoice_due`.
   * @param - Repeated list of event details for bulk reporting.
   */
  (triggerKey: string, eventsInfo: EventInfo[]): Promise<
    NonNullablePaths<
      BulkReportEventResponse,
      | `triggerKey`
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.eventInfo.idempotency.key`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function bulkCancelEvent(
  httpClient: HttpClient
): BulkCancelEventSignature {
  return (triggerKey: string, externalEntityIds: string[]) =>
    universalBulkCancelEvent(
      triggerKey,
      externalEntityIds,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkCancelEventSignature {
  /**
   * Bulk cancels any remaining actions for a trigger and external entities.
   * @param - Trigger key whose events you want to cancel.
   * For example, `form_submitted` or `invoice_due`.
   * @param - Repeated list of external_entity_id, representing the related resources' IDs.
   */
  (triggerKey: string, externalEntityIds: string[]): Promise<
    NonNullablePaths<
      BulkCancelEventResponse,
      | `triggerKey`
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.externalEntityId`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function cancelEvent(httpClient: HttpClient): CancelEventSignature {
  return (triggerKey: string, externalEntityId: string) =>
    universalCancelEvent(
      triggerKey,
      externalEntityId,
      // @ts-ignore
      { httpClient }
    );
}

interface CancelEventSignature {
  /**
   * Cancels any remaining actions for a trigger and external entity.
   *
   *
   * Events are not cancelable by default.
   * To make an event cancelable,
   * you must first pass an `externalEntityId`
   * and the applicable `triggerKey` to [reportEvent()](#report-event).
   * When you call cancelEvent() with the same `externalEntityId` and `triggerKey`,
   * the event is canceled,
   * as are all other events that share the `externalEntityId` and `triggerKey`.
   * @param - Trigger key whose event you want to cancel.
   * For example, `form_submitted` or `invoice_due`.
   * @param - ID of the related resource in GUID format.
   * For example, `fc81a355-3429-50fc-a4c7-def486e828f3`.
   *
   * Typically, this ID is defined in your system,
   * but you can also use any Wix resource GUID,
   * such as contact ID, member ID, or invoice ID.
   * See
   * [Choose the right `externalEntityId`](https://dev.wix.com/docs/rest/business-management/automations/triggered-events/reporting-and-canceling-events#choose-the-right-externalentityid)
   * for more information.
   */
  (triggerKey: string, externalEntityId: string): Promise<void>;
}

/** @internal */
export function rerunActivation(
  httpClient: HttpClient
): RerunActivationSignature {
  return (activationId: string, options?: RerunActivationOptions) =>
    universalRerunActivation(
      activationId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface RerunActivationSignature {
  /**
   * Rerun an automation using a previous activation's payload.
   * @param - The activation ID whose payload will be used for the rerun.
   */
  (
    activationId: string,
    options?: RerunActivationOptions
  ): Promise<RerunActivationResponse>;
}

export function reportActionInvocationCompleted(
  httpClient: HttpClient
): ReportActionInvocationCompletedSignature {
  return (
    status: StatusWithLiterals,
    invocationToken: string,
    options?: ReportActionInvocationCompletedOptions
  ) =>
    universalReportActionInvocationCompleted(
      status,
      invocationToken,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ReportActionInvocationCompletedSignature {
  /**
   * > **Note**: Call this method when using the [Action Provider service plugin](https://dev.wix.com/docs/api-reference/business-management/automations/actions/action-provider-service-plugin/introduction) to run an action with a timeout.
   *
   * Reports to Wix that your action has completed.
   *
   * When Wix calls [Invoke](https://dev.wix.com/docs/api-reference/business-management/automations/actions/action-provider-service-plugin/invoke) to run your [action that has a timeout](https://dev.wix.com/docs/api-reference/business-management/automations/actions/action-provider-service-plugin/introduction#action-timeouts), it pauses the automation until you call this method or until the timeout period ends. If your action has an [output schema](https://dev.wix.com/docs/api-reference/business-management/automations/actions/about-actions#the-output-schema), include your action's results.
   *
   * If you don't call this method in the timeout period, Wix [marks the action as failed](https://support.wix.com/en/article/wix-automations-understanding-run-logs#understanding-statuses) and continues to the next step in the automation. Subsequent actions may fail if they rely on your action's output.
   * @param - Action completion status.
   * @param - Invocation token from Wix's original call to [Invoke](https://dev.wix.com/docs/api-reference/business-management/automations/actions/action-provider-service-plugin/invoke). Wix uses this token to match your results with the correct automation run.
   *
   * Learn more about [running an invoked action with a timeout](https://dev.wix.com/docs/api-reference/business-management/automations/actions/action-provider-service-plugin/sample-flow#run-an-invoked-action-with-a-timeout).
   */
  (
    status: StatusWithLiterals,
    invocationToken: string,
    options?: ReportActionInvocationCompletedOptions
  ): Promise<void>;
}

export {
  AIMetadata,
  AccountInfo,
  Action,
  ActionActionOneOf,
  ActionAttributes,
  ActionCodeSnippet,
  ActionCompletedRequest,
  ActionData,
  ActionEvent,
  ActionRetryRequest,
  ActionSettings,
  ActionSplitAction,
  ActionsData,
  Activation,
  ActivationActionStatusChanged,
  ActivationActionStatusChangedInfoOneOf,
  ActivationActionStatusChangedStatus,
  ActivationActionStatusChangedStatusInfoOneOf,
  ActivationContext,
  ActivationContinuedAfterSchedule,
  ActivationInitiationSource,
  ActivationInitiationSourceInfo,
  ActivationInitiationSourceInfoInitiationSourceInfoOneOf,
  ActivationRequest,
  ActivationResumeAfterDelay,
  ActivationScheduleCompleted,
  ActivationSource,
  ActivationSourceOfOneOf,
  ActivationStatusChanged,
  ActivationStatusChangedFailedStatusInfo,
  ActivationStatusChangedStartedStatusInfo,
  ActivationStatusChangedStatus,
  ActivationStatusChangedStatusInfoOneOf,
  AppDefinedAction,
  AppDefinedActionInfo,
  ApplicationError,
  ApplicationOrigin,
  AsyncAction,
  AuditInfo,
  AuditInfoIdOneOf,
  AutoArchivePolicy,
  Automation,
  AutomationConfiguration,
  AutomationConfigurationAction,
  AutomationConfigurationActionInfoOneOf,
  AutomationConfigurationStatus,
  AutomationIdentifier,
  AutomationInfo,
  AutomationInfoOriginInfoOneOf,
  AutomationInfoTriggerInfo,
  AutomationOriginInfoOneOf,
  AutomationSettings,
  BatchActivationRequest,
  BlockType,
  BulkActionMetadata,
  BulkCancelEventRequest,
  BulkCancelEventResponse,
  BulkCancelEventResult,
  BulkReportEventRequest,
  BulkReportEventResponse,
  BulkReportEventResult,
  CancelEventRequest,
  CancelEventResponse,
  CancelPendingScheduleRequest,
  CancelPendingScheduleRequestByOneOf,
  CancelPendingScheduleResponse,
  CancellationReason,
  CancelledStatusInfo,
  Case,
  CodeConditionAction,
  CodeConditionActionInfo,
  CodeConditionFilter,
  CodeSnippet,
  CodeSnippetLanguage,
  ConditionAction,
  ConditionActionInfo,
  ConditionBlock,
  ConditionExpressionGroup,
  ConditionFilter,
  ConsumeReportEventRequest,
  Delay,
  DelayAction,
  DelayActionInfo,
  DelayHelper,
  DelayOfOneOf,
  Dimensions,
  Domain,
  DomainEvent,
  DomainEventBodyOneOf,
  DraftInfo,
  Empty,
  EndedStatusInfo,
  EndedStatusInfoAppDefinedActionInfo,
  EndedStatusInfoTypeInfoOneOf,
  Enrichment,
  Enrichments,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  ErrorReason,
  EventInfo,
  ExecuteFromActionRequest,
  ExecuteFromActionResponse,
  ExpressionEvaluationResult,
  ExtendedFields,
  FailedStatusInfo,
  FailureInfo,
  Filter,
  FilterValueSelection,
  FilterableAppDefinedActions,
  FutureDateActivationOffset,
  GetScheduleRequest,
  GetScheduleResponse,
  Idempotency,
  IdentificationData,
  IdentificationDataIdOneOf,
  IdentifierType,
  Identity,
  IfFilter,
  InitiatedStatusInfo,
  InitiatorInfo,
  InvokedAsyncStatusInfo,
  InvokedAsyncStatusInfoAppDefinedActionInfo,
  InvokedAsyncStatusInfoTypeInfoOneOf,
  ItemMetadata,
  Language,
  ManualActivationInfo,
  MergeAction,
  MessageEnvelope,
  Metadata,
  Note,
  Notes,
  Offset,
  Operator,
  Origin,
  Output,
  OutputAction,
  Path,
  Position,
  PreinstalledIdentifier,
  PreinstalledOrigin,
  RateLimit,
  RateLimitAction,
  RateLimitActionInfo,
  RateLimiting,
  RefreshPayloadRequest,
  RefreshPayloadResponse,
  ReportAccountLevelEventRequest,
  ReportAccountLevelEventResponse,
  ReportActionInvocationCompletedOptions,
  ReportActionInvocationCompletedOptionsStatusInfoOneOf,
  ReportActionInvocationCompletedRequest,
  ReportActionInvocationCompletedRequestStatusInfoOneOf,
  ReportActionInvocationCompletedResponse,
  ReportDomainEventRequest,
  ReportDomainEventResponse,
  ReportEventOptions,
  ReportEventRequest,
  ReportEventResponse,
  RerunActivationInfo,
  RerunActivationOptions,
  RerunActivationRequest,
  RerunActivationResponse,
  RestoreInfo,
  ResumeAfterAsyncActionCompleted,
  ResumeAfterAsyncActionCompletedStatusInfoOneOf,
  RetryInfo,
  RunAutomationRequest,
  RunAutomationResponse,
  RunOptions,
  RunPreinstalledAutomationRequest,
  RunPreinstalledAutomationResponse,
  Runtime,
  RuntimeStatus,
  Schedule,
  ScheduleActivationRequested,
  ScheduleRequest,
  ScheduleResponse,
  ScheduleStatus,
  ScheduledAction,
  ScheduledStatusInfo,
  Scheduler,
  Service,
  ServiceMapping,
  SetVariables,
  SetVariablesAction,
  SetVariablesActionInfo,
  SimpleDelay,
  SkipReason,
  SkippedStatusInfo,
  SpiAction,
  SplitAction,
  SplitActionPath,
  StartedStatusInfo,
  StartedStatusInfoAppDefinedActionInfo,
  StartedStatusInfoDelayActionInfo,
  StartedStatusInfoTypeInfoOneOf,
  Status,
  SuccessInfo,
  SwitchFilter,
  SystemHelper,
  SystemHelperHelperOneOf,
  Target,
  TestAutomationRequest,
  TestAutomationRequestIdentifierOneOf,
  TestAutomationResponse,
  TimeUnit,
  Trigger,
  TriggerInfo,
  TriggerSettings,
  Type,
  Units,
  UpdatePendingSchedulesPayloadRequest,
  UpdatePendingSchedulesPayloadResponse,
  UpdateScheduleStatusRequest,
  UpdateScheduleStatusResponse,
  V1RunAutomationRequest,
  V1RunAutomationRequestIdentifierOneOf,
  V1RunAutomationResponse,
  WebhookIdentityType,
} from './automations-v2-activation-activations.universal.js';
